// EXEMPLO DE CADA LAYOUT — o pacote que o proprio adaptador reconhece.
//
// Tres consumidores, um conteudo: a rota `GET /v1/admin/layouts/:codigo/exemplo.zip`
// (o que a averbadora baixa para mostrar ao ente), a suite de conformidade
// (C2: "o exemplo e' reconhecido pelo proprio layout e por nenhum outro") e a
// cobertura da matriz ("todo valor declarado e' exercitado por um exemplo").
//
// Sem I/O: o dominio roda no Worker. Os exemplos do padrao saem das mesmas
// funcoes que geram o `exemplo.zip` da carga inicial; os da ALECE sao as
// fixtures sinteticas embutidas como constante (`alece-exemplo.ts`); os de
// saida sao a serializacao de uma remessa de exemplo pelo adaptador de saida.
//
// Um layout sem exemplo aqui falha em `exemplos.test.ts` — e' a regra "codigo
// sem exemplo falha" do plano (T009/T015), fora de `validarCatalogo` para nao
// fechar um ciclo de import (catalogo → exemplos → adaptadores → catalogo).
import { EXEMPLO_COMPETENCIA, exemploCarga, exemploConsignados, exemploRubricas } from "../exemplo-pacote.js";
import { CONTEXTO_SAIDA_VAZIO } from "./adaptador.js";
import { adaptadorAtlasMovimento } from "./atlas/atlas-movimento.js";
import { adaptadorAssalceRetorno } from "./assalce/assalce-retorno.js";
import { adaptadorConsignatariaRetorno } from "./consignataria/consignataria-retorno.js";
import { adaptadorAleceMovimento } from "./alece/alece-movimento.js";
import { ALECE_EXEMPLO_FOLHA, ALECE_EXEMPLO_SERVIDOR } from "./alece/alece-exemplo.js";
import { COLUNAS_CONTRATOS_ALECE } from "./alece/alece-carga-colunas.js";
import { ASSALCE_EXEMPLO_COMPETENCIA, ASSALCE_EXEMPLO_NOME, ASSALCE_EXEMPLO_TEXTO, } from "./assalce/assalce-exemplo.js";
/**
 * A remessa de exemplo dos layouts de SAIDA — a mesma do snapshot
 * `__fixtures__/padrao-movimento.snapshot.json` (T003 da 006), transcrita
 * porque o dominio nao le JSON de disco.
 */
export const EXEMPLO_REMESSA = {
    unidadeId: 1,
    prefeituraNome: "I.M.A.M.N. - Instituto de Meio Ambiente",
    competencia: "202608",
    dataCorte: "2026-08-08",
    geradoEm: "2026-08-27T12:00:00.000Z",
    congelada: true,
    descontos: [
        {
            adf: "9471423",
            matricula: "1300509",
            cpf: "25842153300",
            nome: "BENEDITO NOGUEIRA LIMA",
            credorId: 1,
            credorNome: "BANCO DO BRASIL",
            codigoVerba: "0234",
            valorParcela: 233.17,
            parcelaNumero: 5,
            parcelasTotal: 49,
            origem: "A",
        },
        {
            adf: "9471999",
            matricula: "60406",
            cpf: "70512345691",
            nome: "MARIA JOSE DA SILVA",
            credorId: 0,
            credorNome: "CONSIGNATARIA LEGADA",
            codigoVerba: "0267",
            valorParcela: 1696.87,
            parcelaNumero: 8,
            parcelasTotal: 96,
            origem: "H",
            referencia: "REF-9",
        },
    ],
};
/** A competencia que as fixtures sinteticas da ALECE declaram. */
export const ALECE_EXEMPLO_COMPETENCIA = "202609";
/**
 * A entrada de exemplo do retorno a consignataria (#189): quatro linhas do
 * retorno da ALECE nas rubricas da ASSALCE, ja' separadas por contrato —
 * integral, parcial, desligado sem desconto (sai com 0,00) — nao creditícias,
 * `SEM ADF` — e uma linha de dois contratos de credito, de matricula sem zeros
 * (sai como veio). O destinatario e' o Banco do Brasil, para o arquivo do
 * formato detalhado sair como REBRASIL; o desligado nao tem cadastro, e sai
 * sem nome. Dados inventados, nomes inclusive.
 */
export const RETORNO_CONSIGNATARIA_EXEMPLO = {
    competencia: ALECE_EXEMPLO_COMPETENCIA,
    destinatario: "BANCO DO BRASIL S.A.",
    linhas: [
        { linha: 1, matricula: "001476", cpf: "10000003700", nome: "MARIA DAS GRAÇAS SOUSA", rubrica: "641", credito: false, contrato: null, instruidoCentavos: 660410, descontadoCentavos: 660410, motivo: "PGTO OK" },
        { linha: 2, matricula: "001477", cpf: "10765432196", nome: "JOSÉ RIBAMAR LIMA", rubrica: "913", credito: false, contrato: null, instruidoCentavos: 344553, descontadoCentavos: 4321, motivo: "MARGEM INSUFICIENTE" },
        { linha: 3, matricula: "001478", cpf: "11530864259", nome: "", rubrica: "641", credito: false, contrato: null, instruidoCentavos: 10078, descontadoCentavos: 0, motivo: "DESLIGADO" },
        { linha: 4, matricula: "1479", cpf: "13061728457", nome: "ANTÔNIA FERREIRA", rubrica: "807", credito: true, contrato: "ADF-EX-0001", instruidoCentavos: 25000, descontadoCentavos: 25000, motivo: "MARGEM INSUFICIENTE" },
        { linha: 4, matricula: "1479", cpf: "13061728457", nome: "ANTÔNIA FERREIRA", rubrica: "807", credito: true, contrato: "ADF-EX-0002", instruidoCentavos: 12000, descontadoCentavos: 0, motivo: "MARGEM INSUFICIENTE" },
    ],
};
function deRetornoConsignataria(adaptador, descricao) {
    const g = adaptador.serializar(RETORNO_CONSIGNATARIA_EXEMPLO);
    return {
        descricao,
        competencia: RETORNO_CONSIGNATARIA_EXEMPLO.competencia,
        arquivos: g.arquivos.map((a) => ({ nome: a.nome, texto: a.texto })),
    };
}
function deSaida(adaptador, descricao) {
    const g = adaptador.serializar(EXEMPLO_REMESSA, CONTEXTO_SAIDA_VAZIO);
    return {
        descricao,
        competencia: EXEMPLO_REMESSA.competencia,
        arquivos: g.arquivos.map((a) => a.formato === "planilha"
            ? { nome: a.nome, celulas: a.celulas ?? [] }
            : { nome: a.nome, texto: a.texto }),
    };
}
/**
 * Marca de ordem de bytes, por codigo e nao pelo caractere.
 *
 * Escrito de verdade, o BOM e' um ponto de codigo INVISIVEL no fonte: some num
 * copiar-colar, sobrevive a um `trim()` que parecia inofensivo, e reaparece
 * como "cabecalho ausente" semanas depois. Assim ele se le.
 */
const BOM = String.fromCharCode(0xfeff);
const EXEMPLOS = {
    "atlas-retorno": () => ({
        descricao: "Os três CSVs do pacote de exemplo da carga inicial.",
        competencia: EXEMPLO_COMPETENCIA,
        arquivos: [
            { nome: `arquivocarga${EXEMPLO_COMPETENCIA}.csv`, texto: exemploCarga() },
            { nome: `retorno-consignados${EXEMPLO_COMPETENCIA}.csv`, texto: exemploConsignados() },
            { nome: `rubrica_funcionarios${EXEMPLO_COMPETENCIA}.csv`, texto: exemploRubricas() },
        ],
    }),
    "atlas-movimento": () => deSaida(adaptadorAtlasMovimento, "O movimento de duas dívidas no padrão Atlas."),
    "consignataria-retorno": () => deRetornoConsignataria(adaptadorConsignatariaRetorno, "Quatro linhas do retorno do órgão/entidade nas rubricas do Banco do Brasil viram cinco no REBRASIL: integral, parcial, desligado sem desconto (sem cadastro: nome vazio) e uma linha de dois contratos, de matrícula sem zeros."),
    "assalce-retorno": () => deRetornoConsignataria(adaptadorAssalceRetorno, "Quatro linhas do retorno da ALECE nas rubricas da ASSALCE viram cinco no arquivo: integral, parcial, desligado sem desconto (0,00) e uma linha de dois contratos, cada um com o seu valor."),
    "alece-retorno": () => ({
        descricao: "Base de servidores e extrato de descontos sintéticos da ALECE.",
        competencia: ALECE_EXEMPLO_COMPETENCIA,
        arquivos: [
            { nome: `ALECE-SERVIDOR-${ALECE_EXEMPLO_COMPETENCIA.slice(2)}.TXT`, texto: ALECE_EXEMPLO_SERVIDOR },
            { nome: `ALECE-FOLHA-${ALECE_EXEMPLO_COMPETENCIA.slice(2)}.TXT`, texto: ALECE_EXEMPLO_FOLHA },
        ],
    }),
    // A carga inicial usa o MESMO texto do retorno mensal com DOIS ajustes, e os
    // dois sao o que a competencia 202609 trouxe de verdade: a extensao e' `.CSV`
    // e o arquivo comeca com marca de ordem de bytes. O BOM entra aqui, e nao na
    // constante, porque a constante e' compartilhada com o exemplo do retorno
    // mensal — e la' ele nao existe.
    "alece-carga-inicial": () => ({
        descricao: "Carga inicial completa sintética da ALECE.",
        competencia: ALECE_EXEMPLO_COMPETENCIA,
        arquivos: [
            {
                nome: `ALECE-SERVIDOR-${ALECE_EXEMPLO_COMPETENCIA.slice(2)}.CSV`,
                texto: `${BOM}${ALECE_EXEMPLO_SERVIDOR}`,
            },
            { nome: `ALECE-FOLHA-${ALECE_EXEMPLO_COMPETENCIA.slice(2)}.CSV`, texto: `${BOM}${ALECE_EXEMPLO_FOLHA}` },
            { nome: `ALECE-CONTRATOS-${ALECE_EXEMPLO_COMPETENCIA.slice(2)}.xlsx`, celulas: [
                    [...COLUNAS_CONTRATOS_ALECE],
                    ["230951", "600404533", "Empréstimo Consignado (120)", "31/08/2026", "Banco Industrial do Brasil", "854", "1300000", "100000037", "Assembleia Legislativa do Ceará", "SERVIDOR SINTETICO 01", "Setembro de 2026", "60", "60", "120", "7200", "5000,00", "Ativo", "1 / Setembro de 2026", "Agosto de 2031", "Novo contrato"],
                ] },
        ],
    }),
    "alece-movimento": () => deSaida(adaptadorAleceMovimento, "O movimento de duas dívidas na estrutura do extrato da ALECE."),
    "assalce-entrada": () => ({
        descricao: "Um ASS202609.TXT sintético com planos, avulsas, averbação com prazo, valor zero, uma linha torta e linhas vazias no fim.",
        competencia: ASSALCE_EXEMPLO_COMPETENCIA,
        arquivos: [{ nome: ASSALCE_EXEMPLO_NOME, texto: ASSALCE_EXEMPLO_TEXTO }],
    }),
};
/** Registra o exemplo de um layout de teste (chamado pelo proprio modulo do layout, em `teste/`). */
export function registrarExemplo(codigo, fabrica) {
    EXEMPLOS[codigo] = fabrica;
}
/** Lanca para codigo sem exemplo — e' o que `exemplos.test.ts` transforma em portao. */
export function exemploDoLayout(codigo) {
    const f = EXEMPLOS[codigo];
    if (!f)
        throw new Error(`layout sem exemplo: "${codigo}"`);
    return f();
}
export function codigosComExemplo() {
    return Object.keys(EXEMPLOS);
}
//# sourceMappingURL=exemplos.js.map